import { call, SagaGenerator } from 'typed-redux-saga';
import { dossiersApiClient, findingsApiClient } from '@owl-frontend/api-client';
import type { Dossier, Finding } from '@owl-frontend/api-client/interface';
import { asyncActionStateMatchers, invoke } from '@owl-frontend/redux';
import dossierDetailsSlice from './interface';

function* concludeDossier(action: {
  payload: {
    dossierId: string;
    groupId: string;
    conclusion: Dossier['conclusion'];
    findings: Finding[];
    conclusionDetails: Dossier['conclusionDetails'];
  };
}): SagaGenerator<Dossier> {
  const { groupId, dossierId, conclusion, conclusionDetails, findings } =
    action.payload;

  return yield* call(dossiersApiClient.dossiers.conclude, {
    groupId,
    dossierId,
    conclusion,
    conclusionDetails,
    findings: findings.map((f) => ({
      findingId: f.findingId,
      decision: f.decision,
      decisionDetails: f.decisionDetails,
    })),
  });
}

function* fetchDossierFindings(action: {
  payload: {
    dossierId: string;
  };
}): SagaGenerator<{
  dossierId: string;
  items: Finding[];
}> {
  const { dossierId } = action.payload;
  return {
    dossierId,
    items: yield* call(findingsApiClient.findings.list, {
      dossierId,
    }),
  };
}

const slice = dossierDetailsSlice
  .addAsyncSagas({
    fetchDossierDetails: invoke(dossiersApiClient.dossiers.get),
    setFindingDecision: invoke(findingsApiClient.findings.setDecision),
    assignmentCount: invoke(dossiersApiClient.reservations.assignmentCount),
    reservationDetails: invoke(
      dossiersApiClient.reservations.reservationDetails
    ),
    assignDossier: invoke(dossiersApiClient.reservations.assignDossier),
    snoozeDossier: invoke(dossiersApiClient.reservations.snooze),
    snoozeCount: invoke(dossiersApiClient.reservations.snoozeCount),
    fetchDossierFindings,
    concludeDossier,
    doneDossier: invoke(dossiersApiClient.dossiers.done),
    closeDossier: invoke(dossiersApiClient.dossiers.close),
  })
  .addReducers({
    'fetchDossierDetails/fulfilled': (state, action) => {
      state.dossiers[action.payload.dossierId] = action.payload;
    },
    'fetchDossierFindings/fulfilled': (state, action) => {
      state.findings[action.payload.dossierId] = action.payload.items ?? [];
    },
    'fetchDossierFindings/rejected': (state, action) => {
      state.findings[action.meta.arg.dossierId] = [];
    },
    'assignmentCount/fulfilled': (state, action) => {
      state.assignmentCount = {
        count: action.payload.count,
        maxReassignments: action.payload.maxReassignments,
      };
    },
    'reservationDetails/fulfilled': (state, action) => {
      state.reservationDetails = {
        [action.meta.arg.dossierId]: action.payload,
      };
    },
    'snoozeCount/fulfilled': (state, action) => {
      state.snoozeCount = {
        count: action.payload.count,
        maxSnoozes: action.payload.maxSnoozes,
      };
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
